// A purveyor's catalog: the products its boxes can list (0052, feedback #58).
// Edited by the purveyor's own members from the Purveyor Portal.
//
// Availability gets its own control on each row, since "sold out" is the
// change a farm makes most often; everything else goes through Edit.
// Deleting a product that an open box still uses keeps the item's name in
// that box (a trigger in 0052 copies it into the item as a one-off), and the
// local copy of the boxes is updated the same way here.
import { useState } from "react";
import { CATEGORIES, CATEGORY_LABELS, PRODUCT_AVAILABILITY, INVENTORY_UNIT_SUGGESTIONS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import { createProduct, updateProduct, deleteProduct } from "../lib/db";
import { editorStyles, openBoxesUsing } from "../lib/purveyorEditing";
import { capitalizeFirst, formatQuantity } from "../lib/utils";

const blankProduct = () => ({ name: "", description: "", category: "", groceryCategory: "produce", quantity: "", unit: "", availability: "available", startDate: "", endDate: "" });

export function PurveyorCatalogEditor({ purveyorId, purveyor, onChange, colors, isMobile }) {
  const s = editorStyles(colors);
  const { card, line, soft, danger } = colors;
  const [editingId, setEditingId] = useState(null); // a product id, "new", or null
  const [draft, setDraft] = useState(blankProduct());
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const products = [...purveyor.products].sort((a, b) => a.name.localeCompare(b.name));
  const setProducts = (updater) => onChange((p) => ({ ...p, products: updater(p.products) }));

  function startEdit(product) {
    setEditingId(product ? product.id : "new");
    setDraft(product ? { ...product, quantity: product.quantity ? String(product.quantity) : "" } : blankProduct());
    setConfirmDeleteId(null);
    setError("");
  }

  async function run(id, action) {
    setBusyId(id);
    setError("");
    try {
      await action();
      return true;
    } catch (err) {
      setError(err.message || "Couldn't save — try again.");
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function save() {
    const ok = await run(editingId, async () => {
      if (editingId === "new") {
        const created = await createProduct(purveyorId, draft);
        setProducts((list) => [...list, created]);
      } else {
        const updated = await updateProduct(editingId, draft);
        setProducts((list) => list.map((p) => (p.id === editingId ? updated : p)));
      }
    });
    if (ok) setEditingId(null);
  }

  const setAvailability = (product, availability) =>
    run(product.id, async () => {
      const updated = await updateProduct(product.id, { ...product, availability });
      setProducts((list) => list.map((p) => (p.id === product.id ? updated : p)));
    });

  async function remove(product) {
    const ok = await run(product.id, () => deleteProduct(product.id));
    if (!ok) return;
    setConfirmDeleteId(null);
    // Mirror 0052's trigger and 0047's ON DELETE SET NULL in the local copy.
    onChange((p) => ({
      ...p,
      products: p.products.filter((x) => x.id !== product.id),
      boxes: p.boxes.map((b) => ({
        ...b,
        items: b.items.map((it) =>
          it.productId !== product.id ? it : { ...it, productId: null, oneOffName: b.status !== "Delivered" && !it.oneOffName ? product.name : it.oneOffName }
        ),
      })),
    }));
  }

  const describe = (p) =>
    [
      p.quantity ? `${formatQuantity(p.quantity)} ${p.unit}`.trim() : p.unit,
      p.category,
      CATEGORY_LABELS[p.groceryCategory] || p.groceryCategory,
      p.startDate || p.endDate ? `${p.startDate || "…"} to ${p.endDate || "…"}` : "",
    ]
      .filter(Boolean)
      .join(" · ");

  const form = (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md, background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
      <div style={s.grid(isMobile, "2fr 1fr 1fr")}>
        <label style={s.label}>
          Name
          <input style={s.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
        </label>
        <label style={s.label}>
          Quantity
          <input style={s.input} type="number" min="0" step="any" value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} />
        </label>
        <label style={s.label}>
          Unit
          <input style={s.input} list="catalog-unit-suggestions" placeholder="lb, bunch, dozen…" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} />
        </label>
        <label style={s.label}>
          Your category
          <input style={s.input} placeholder="e.g. Greens, Roots" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
        </label>
        <label style={s.label}>
          Grocery aisle
          <select style={s.input} value={draft.groceryCategory} onChange={(e) => setDraft({ ...draft, groceryCategory: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </label>
        <label style={s.label}>
          Availability
          <select style={s.input} value={draft.availability} onChange={(e) => setDraft({ ...draft, availability: e.target.value })}>
            {PRODUCT_AVAILABILITY.map((a) => <option key={a} value={a}>{capitalizeFirst(a)}</option>)}
          </select>
        </label>
        <label style={s.label}>
          Available from
          <input style={s.input} type="date" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
        </label>
        <label style={s.label}>
          Available until
          <input style={s.input} type="date" value={draft.endDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
        </label>
      </div>
      <label style={s.label}>
        Description
        <textarea style={{ ...s.input, minHeight: 56, resize: "vertical" }} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      </label>
      <div style={{ display: "flex", gap: SPACE.md }}>
        <button className="mp-btn" disabled={busyId !== null} onClick={save} style={s.primary}>{editingId === "new" ? "Add product" : "Save"}</button>
        <button className="mp-btn" disabled={busyId !== null} onClick={() => setEditingId(null)} style={s.secondary}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
      <datalist id="catalog-unit-suggestions">
        {INVENTORY_UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
      </datalist>
      <p style={{ margin: 0, color: soft, fontSize: FONT_SIZE.sm, maxWidth: 640 }}>
        The products you offer. Boxes pick items from this list, and households see it on your Purveyors page.
      </p>
      {editingId === "new" ? form : (
        <div>
          <button className="mp-btn" disabled={editingId !== null} onClick={() => startEdit(null)} style={s.primary}>+ Add product</button>
        </div>
      )}
      {products.length === 0 && editingId !== "new" && <p style={{ margin: 0, color: soft }}>No products yet.</p>}
      {products.map((p) => {
        if (editingId === p.id) return <div key={p.id}>{form}</div>;
        const openBoxes = openBoxesUsing(purveyor.boxes, p.id);
        const busy = busyId === p.id;
        return (
          <div key={p.id} style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.lg, padding: "var(--space-lg) var(--space-xl2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: p.availability === "sold out" ? soft : colors.ink }}>{p.name}</div>
                <div style={{ fontSize: FONT_SIZE.sm, color: soft }}>{describe(p)}</div>
              </div>
              <div style={{ display: "flex", gap: SPACE.sm, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  aria-label={`Availability of ${p.name}`}
                  style={{ ...s.input, width: "auto", color: p.availability === "sold out" ? danger : colors.ink }}
                  value={p.availability}
                  disabled={busy || editingId !== null}
                  onChange={(e) => setAvailability(p, e.target.value)}
                >
                  {PRODUCT_AVAILABILITY.map((a) => <option key={a} value={a}>{capitalizeFirst(a)}</option>)}
                </select>
                <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => startEdit(p)} style={s.secondary}>Edit</button>
                <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => setConfirmDeleteId(p.id)} style={s.danger}>Delete</button>
              </div>
            </div>
            {p.description && <p style={{ margin: "var(--space-sm) 0 0", fontSize: FONT_SIZE.sm }}>{p.description}</p>}
            {confirmDeleteId === p.id && (
              <div style={{ marginTop: SPACE.lg, display: "flex", flexDirection: "column", gap: SPACE.md, fontSize: FONT_SIZE.sm, color: danger }}>
                <span>
                  Delete {p.name} from your catalog?
                  {openBoxes.length > 0
                    ? ` ${openBoxes.length === 1 ? "An open box lists" : `${openBoxes.length} open boxes list`} it; ${openBoxes.length === 1 ? "that box keeps" : "those boxes keep"} the item under the same name.`
                    : ""}
                  {" "}If you just don't have it right now, marking it Sold out keeps it for next time.
                </span>
                <span style={{ display: "flex", gap: SPACE.md }}>
                  <button className="mp-btn" disabled={busy} onClick={() => remove(p)} style={{ ...s.primary, background: danger }}>Delete</button>
                  <button className="mp-btn" disabled={busy} onClick={() => setConfirmDeleteId(null)} style={s.secondary}>Cancel</button>
                </span>
              </div>
            )}
          </div>
        );
      })}
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}
